import type React from 'react';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../config';
import styles from './VulnerabilityDetail.module.css';

interface SimilarVulnerabilitiesModalProps {
  similarVulns: any[];
  isLoading: boolean;
  fpReason: string;
  fpSummary: string;
  onClose: () => void;
}

export const SimilarVulnerabilitiesModal: React.FC<SimilarVulnerabilitiesModalProps> = ({
  similarVulns,
  isLoading,
  fpReason,
  fpSummary,
  onClose,
}) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const handleBatchMark = async () => {
    if (selectedIds.length === 0) {
      toast.warning('Please select vulnerabilities to mark');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/vulnerabilities/batch-false-positive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vulnerability_ids: selectedIds,
          reason: fpReason,
          summary: fpSummary,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Marked ${result.data.updated_count} vulnerabilities as false positive`);
        onClose();
      } else {
        toast.error('Failed to batch mark false positives');
      }
    } catch (error) {
      console.error('Error batch marking:', error);
      toast.error('Failed to batch mark false positives');
    }
  };

  const toggleSelected = (id: string, checked: boolean) => {
    setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((sid) => sid !== id)));
  };

  return (
    <div className={styles.modal} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>Similar Vulnerabilities</h3>
          <button className={styles.actionButton} data-variant="secondary" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className={styles.modalBody}>
          {isLoading ? (
            <div className={styles.emptyState}>
              <h3>Analyzing vulnerabilities...</h3>
              <p>Using AI to find similar vulnerabilities</p>
            </div>
          ) : similarVulns.length === 0 ? (
            <div className={styles.emptyState}>
              <h3>No similar vulnerabilities found</h3>
              <p>No vulnerabilities with high similarity were detected</p>
            </div>
          ) : (
            <>
              <p className={styles.selectHint}>Select vulnerabilities to mark as false positive:</p>
              <div className={styles.similarVulnList}>
                {similarVulns.map((vuln) => (
                  <div className={styles.similarVulnItem} key={vuln.id}>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(vuln.id)}
                      onChange={(e) => toggleSelected(vuln.id, e.target.checked)}
                    />
                    <label className={styles.checkboxLabel}>
                      <div className={styles.vulnInfo}>
                        <div className={styles.vulnTitle}>{vuln.title}</div>
                        <div className={styles.vulnMeta}>
                          {vuln.vulnerability_type} • {vuln.endpoint} • {vuln.severity}
                        </div>
                        <div className={styles.similarityScore}>
                          Similarity: {(vuln.similarity_score * 100).toFixed(1)}% - {vuln.reason}
                        </div>
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <div className={styles.modalFooter}>
          <button className={styles.actionButton} data-variant="secondary" onClick={onClose}>
            Close
          </button>
          {!isLoading && similarVulns.length > 0 && (
            <button className={styles.actionButton} onClick={handleBatchMark} disabled={selectedIds.length === 0}>
              Mark {selectedIds.length} as False Positive
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
